/* istanbul ignore file */

import { useCallback } from 'react';

import type { Region } from '@sg/garnish';
import { useDebounceFn } from '@sg/garnish';

import { type CompleteAddressForm } from '../../components';
import { getLocationOrderChannel } from '../../helpers';
import { useInitLocationSearchMachine } from '../useInitLocationSearchMachine';

import type {
  AddressDetails,
  AddressWithoutIds,
  Location,
  LocationNavigationEntryPoint,
  LocationSearchMachineContext,
} from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useLocationSearchMachine = (props: UseLocationSearchMachineProps) => {
  const { navigateToLocation, navigateToDeliveryLocation, shouldRequestLocationPermission } = props;

  // ─────────────────────────────────────────────────────────────────────

  const [state, send, service] = useInitLocationSearchMachine({
    navigateToLocation,
    navigateToDeliveryLocation,
    shouldRequestLocationPermission,
  });

  const { context } = state;

  // ─── Derived Data ────────────────────────────────────────────────────

  const hasActiveLocation = context.activeLocation !== undefined;

  const isLoadingPredictions = state.matches({ search: { loading: 'searchDeliveryAddresses' } });
  const isLoadingLocations =
    (state.matches({ search: 'loading' }) && !isLoadingPredictions) ||
    state.matches({ searchForPredictions: 'selecting' });
  const isAddingAddress = state.matches({ search: { loading: 'addNewDeliveryAddress' } });

  const shouldShowLocationWarning =
    (state.matches({ search: { warning: 'pickupLocation' } }) ||
      state.matches({ search: { warning: 'outpostLocation' } })) &&
    hasActiveLocation;

  const isLoadingRecentAndNearbyLocations =
    state.matches({ search: { setup: 'setupRecentAndNearbyLocations' } }) ||
    state.matches({ search: { setup: 'setupNearbyLocations' } });

  const shouldRenderRecentAndNearbyLocations =
    !state.matches({ searchType: 'delivery' }) &&
    (state.matches({ search: 'recentAndNearbyLocations' }) || isLoadingRecentAndNearbyLocations);

  const shouldRenderDeliveryAddressForm = state.matches({ search: 'showingDeliveryAddressForm' });

  // This indicates that the user used the location search field to search
  // for locations.
  const isUsingSearchField = state.matches({ searchForPredictions: 'searching' });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const setLocationSearchType = useCallback(
    (searchType: string) => {
      if (searchType === 'pickup') send({ type: 'SET_LOCATION_SEARCH_TYPE_PICKUP' });

      if (searchType === 'delivery') send({ type: 'SET_LOCATION_SEARCH_TYPE_DELIVERY' });

      if (searchType === 'outpost') send({ type: 'SET_LOCATION_SEARCH_TYPE_OUTPOST' });
    },
    [send],
  );

  const searchBasedOnCurrentPosition = useCallback(() => {
    send({ type: 'SEARCH_BY_CURRENT_POSITION' });
  }, [send]);

  const searchLocationsByArea = useCallback(() => {
    send({ type: 'SEARCH_BY_AREA' });
  }, [send]);

  const searchDeliveryAddresses = useDebounceFn(
    useCallback(
      (searchString: string) => {
        send({ type: 'SET_DELIVERY_SEARCH_STRING', searchString });
      },
      [send],
    ),
    200,
  );

  const setLocationSearchArea = useCallback(
    (region: Region) => {
      const { topLeft, bottomRight } = region;

      if (!topLeft || !bottomRight) return;

      send({ type: 'SET_SEARCH_AREA', searchArea: { topLeft, bottomRight } });
    },
    [send],
  );

  const setDeliveryPredictionPlaceId = useCallback(
    (placeId: string, placeAddress: string) => {
      send({ type: 'SET_DELIVERY_PREDICTION_PLACE_ID', placeId, placeAddress });
    },
    [send],
  );

  const setDeliveryPredictionDetails = useCallback(
    (predictedAddress: AddressWithoutIds) => {
      send({ type: 'SET_DELIVERY_PREDICTION_DETAILS', predictedAddress });
    },
    [send],
  );

  const setDeliveryCustomerAddress = useCallback(
    (addressDetails: AddressDetails) => {
      send({ type: 'SET_DELIVERY_CUSTOMER_ADDRESS', addressDetails });
    },
    [send],
  );

  const setFocusedLocation = useCallback(
    (id?: string) => {
      send({ type: 'SET_FOCUSED_PIN_ID', id });
      send({ type: 'SET_FOCUSED_CARD_ID', id });
    },
    [send],
  );

  const setFocusedLocationPin = useCallback(
    (id: string | undefined) => {
      send({ type: 'SET_FOCUSED_PIN_ID', id });
    },
    [send],
  );

  const confirmLocationWarning = useCallback(() => {
    send({ type: 'LOCATION_WARNING_CONFIRM' });
  }, [send]);

  const cancelLocationWarning = useCallback(() => {
    send({ type: 'LOCATION_WARNING_CANCEL' });
  }, [send]);

  const navigateToSelectedLocation = useCallback(
    ({ restaurantSlug }: LocationRestaurantData, options?: LocationNavigationOptions) => {
      send({
        type: 'NAVIGATE_TO_LOCATION',
        restaurantSlug: restaurantSlug ?? '',
        entryPoint: options?.entryPoint,
      });
    },
    [send],
  );

  const cancelDeliveryAddressForm = useCallback(() => {
    send({ type: 'CANCEL_DELIVERY_ADDRESS_FORM' });
  }, [send]);

  const submitDeliveryAddressForm = useCallback(
    (address: CompleteAddressForm) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation) return;

      send({
        type: 'SUBMIT_DELIVERY_ADDRESS_FORM',
        address: {
          ...deliveryLocation,

          // new parameters that were submitted from the form.
          secondaryStreet: address.secondaryStreet,
          name: address.name,
          notes: address.notes,
          deliveryPreference: address.deliveryPreference,
        },
      });
    },
    [context, send],
  );

  const navigateToSelectedDeliveryLocation = useCallback(
    (_location: LocationRestaurantData, options?: LocationNavigationOptions) => {
      send({
        type: 'NAVIGATE_TO_DELIVERY_LOCATION',
        entryPoint: options?.entryPoint,
      });
    },
    [send],
  );

  const navigateToRecentOrNearbyLocation = useCallback(
    (params: NavigateToLocationParams) => {
      const { location, options } = params;

      const locationOrderChannel = getLocationOrderChannel(location);

      if (locationOrderChannel === 'pickup') {
        send({
          type: 'NAVIGATE_TO_RECENT_PICKUP_LOCATION',
          restaurantSlug: location.restaurantSlug,
          entryPoint: options?.entryPoint,
        });

        return;
      }

      if (locationOrderChannel === 'outpost') {
        send({
          type: 'NAVIGATE_TO_RECENT_OUTPOST_LOCATION',
          restaurantSlug: location.restaurantSlug,
          entryPoint: options?.entryPoint,
        });

        return;
      }

      send({
        type: 'NAVIGATE_TO_RECENT_DELIVERY_LOCATION',
        addressId: location.addressId ?? '',
        entryPoint: options?.entryPoint,
      });
    },
    [send],
  );

  const showRecentAndNearbyLocations = useCallback(() => {
    send({ type: 'SHOW_RECENT_AND_NEARBY_LOCATIONS' });
  }, [send]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    state,
    service,
    flags: {
      isLoadingPredictions,
      isLoadingLocations,
      isAddingAddress,
      isLoadingRecentAndNearbyLocations,
      isUsingSearchField,
      shouldShowLocationWarning,
      shouldRenderRecentAndNearbyLocations,
      shouldRenderDeliveryAddressForm,
    },
    helpers: {
      setLocationSearchType,
      searchBasedOnCurrentPosition,
      searchLocationsByArea,
      searchDeliveryAddresses,
      setLocationSearchArea,
      setDeliveryPredictionPlaceId,
      setDeliveryPredictionDetails,
      setDeliveryCustomerAddress,
      setFocusedLocation,
      setFocusedLocationPin,
      confirmLocationWarning,
      cancelLocationWarning,
      navigateToSelectedLocation,
      cancelDeliveryAddressForm,
      submitDeliveryAddressForm,
      navigateToSelectedDeliveryLocation,
      navigateToRecentOrNearbyLocation,
      showRecentAndNearbyLocations,
    },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseLocationSearchMachineProps = {
  /*
    Optional actions that initiates the navigation process for the selected location.
    When those properties are not provided, the standard navigation is used.

    Helpful when a custom navigation method is utilized or when other actions
    need to be performed before navigation.
   */

  navigateToLocation?: (context: LocationSearchMachineContext) => void;
  navigateToDeliveryLocation?: (context: LocationSearchMachineContext) => void;

  /**
   * When `false`, the machine skips the setup phase that would otherwise
   * trigger the OS location permission request. Defaults to `true`.
   */
  shouldRequestLocationPermission?: boolean;
};

type LocationRestaurantData = { restaurantSlug?: string };

type NavigateToLocationParams = {
  location: Location;
  options?: LocationNavigationOptions;
};

type LocationNavigationOptions = {
  entryPoint?: LocationNavigationEntryPoint;
};
